import { GridColDef } from '@mui/x-data-grid'
import { API_DATE_FORMAT, ExploreHighSchool, isValidSortKey } from './types'
import { PreviewNameCell } from '@scoir/data-table'
import { Box } from '@material-ui/core'
import { parse } from 'date-fns'
import ScoirBadge from 'components/ScoirBadge/ScoirBadge'
import SchoolTypePill from '../components/SchoolTypePill'
import { TITLE_1_PROGRAM_OPTIONS } from '../title-one'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar } from '@fortawesome/pro-regular-svg-icons'
import { encodeSelectedSchools } from '../encode-availability-link'

interface ExploreTableColumns {
  onClickPreview: (highschoolId: string | null) => void
  showPremiumColumns: boolean
}

const enforceSortable = (def: GridColDef<ExploreHighSchool>) => ({
  ...def,
  sortable: isValidSortKey(def.field),
})

function CellData({ children }: { children: React.ReactNode }) {
  return (
    <Box
      overflow="hidden"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
    >
      {children ? children : '—'}
    </Box>
  )
}

const getColumns = ({ onClickPreview, showPremiumColumns }: ExploreTableColumns) => {
  const columns: GridColDef<ExploreHighSchool>[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      renderCell: (params) => (
        <PreviewNameCell
          text="View"
          name={params.row.name}
          setPreview={() => {
            onClickPreview(params.row.id)
          }}
        />
      ),
    },
    {
      field: 'ceebCode',
      headerName: 'CEEB Code',
      width: 100,
      renderCell: (params) => {
        return <CellData>{params.row.ceebCode}</CellData>
      },
    },
    {
      field: 'state',
      headerName: 'State',
      width: 100,
      renderCell: (params) => {
        return <CellData>{params.row.state}</CellData>
      },
    },
    {
      field: 'city',
      headerName: 'City',
      width: 150,
    },
    {
      field: 'schoolType',
      headerName: 'Type',
      minWidth: 90,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.schoolType ? <SchoolTypePill schoolType={params.row.schoolType} /> : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'isActive',
      headerName: 'On Scoir',
      minWidth: 120,
      renderCell: (params) => (params.row.isActive ? <ScoirBadge /> : ''),
    },
    {
      field: 'nextVisitDate',
      headerName: 'Next Visit Date',
      width: 250,
      renderCell: (params) => {
        return (
          <CellData>
            {params.row.nextVisitDate ? (
              <Link
                to={`${import.meta.env.BASE_URL}/recruit/visits/view/week?${encodeSelectedSchools(
                  [{ id: params.row.id, name: params.row.name }],
                  params.row.nextVisitDate,
                )}`}
              >
                <Box
                  mr={1}
                  display="inline-block"
                >
                  <FontAwesomeIcon icon={faCalendar} />
                </Box>
                {Intl.DateTimeFormat('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  weekday: 'long',
                }).format(parse(params.row.nextVisitDate, API_DATE_FORMAT, new Date()))}
              </Link>
            ) : undefined}
          </CellData>
        )
      },
    },
    {
      field: 'isTitle1Eligible',
      headerName: 'Title 1 Eligible',
      width: 150,
      renderCell: (params) => {
        // This is a optional boolean, but we want to show 'Yes' or 'No'
        const val = params.row.isTitle1Eligible
        return <CellData>{val === undefined ? undefined : val ? 'Yes' : 'No'}</CellData>
      },
    },
    {
      field: 'religiousAffiliation',
      headerName: 'Religious Affiliation',
      width: 200,
      renderCell: (params) => {
        return <CellData>{params.row.religiousAffiliation}</CellData>
      },
    },
    {
      field: 'seniorClassSize',
      hide: !showPremiumColumns,
      headerName: 'Senior Class',
      width: 150,
    },
    {
      field: 'title1Program',
      hide: !showPremiumColumns,
      headerName: 'Title 1 Program',
      width: 200,
      renderCell: (params) => {
        const val = params.row.title1Program
        return <CellData>{val ? TITLE_1_PROGRAM_OPTIONS[val].label : undefined}</CellData>
      },
    },
    {
      field: 'pctFreeReducedLunch',
      hide: !showPremiumColumns,
      headerName: '% Free or Reduced Lunch',
      width: 210,
      renderCell: (params) => {
        return percentFormat(params.row.pctFreeReducedLunch)
      },
    },
  ]
  return columns.map(enforceSortable)
}

export function percentFormat(num?: number) {
  if (!num || num === 0) {
    return '0%' // NOTE: Shouldn't happen based on usage, but just in case.
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  return formatter.format(num)
}

export default getColumns
